import { Suspense, useEffect } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import PagePreloader from 'modules/shared/components/loaders/PagePreloader';
import ErrorBoundary from 'modules/shared/hocs/ErrorBoundary';
import PageNotFound from 'modules/shared/pages/not-found/Page';

import { DeleteAccountTypeEnum } from 'api/enums';
import AccountRoutes from 'modules/account/AccountRouter';
import AuthRoutes from 'modules/auth/AuthRouter';
import AutokdeskAuthenticatePage from 'modules/auth/pages/autodesk-authenticate/Page';
import GoogleAuthenticatePage from 'modules/auth/pages/google-authenticate/Page';
import MicrosoftAuthenticatePage from 'modules/auth/pages/microsoft-authenticate/Page';
import DashboardRoutes from 'modules/dashboard/DashboardRouter';
import AutodeskIntegrationViewerPage from 'modules/dashboard/pages/autodesk-integration-viewer/Page';
import IFrameIntegrationViewerPage from 'modules/dashboard/pages/iframe-integration-viewer/Page';
import DocumentRoutes from 'modules/documents/DocumentRouter';
import OnBoardingRoutes from 'modules/onboarding/OnBoardingRouter';
import AutodeskOnboardingAuthenticatePage from 'modules/onboarding/pages/autodesk-onboarding-authentication/Page';
import GoogleOnboardingAuthenticatePage from 'modules/onboarding/pages/google-onboarding-authentication/Page';
import MicrosoftOnboardingAuthenticatePage from 'modules/onboarding/pages/microsoft-onboarding-authentication/Page';
import AutodeskAuthorizationPage from 'modules/plugins/pages/autodesk-sso-authorization/Page';
import GoogleAuthorizationPage from 'modules/plugins/pages/google-authorization/Page';
import GridwallViewerPage from 'modules/plugins/pages/grid-wall-viewer/GridwallViewerPage';
import MicrosoftAuthorizationPage from 'modules/plugins/pages/microsoft-sso-authorization/Page';
import PlanGridAuthorizationPage from 'modules/plugins/pages/plan-grid-authorization/Page';
import ProcoreEmbeddedLoginPage from 'modules/plugins/pages/procore-embedded-login/ProcoreEmbeddedLoginPage';
import PulseLoginPage from 'modules/plugins/pages/pulse-login/PulseLoginPage';
import PluginRoutes from 'modules/plugins/PluginsRouter';
import ProgressLineCommunicationRouter from 'modules/progress-line-communication/ProgressLineCommunicationRouter';
import ProgressLineRouter from 'modules/progress-line/ProgressLineRouter';
import PuppeteerRoutes from 'modules/puppeteer/PuppeteerRouter';
import AuthGuard from 'modules/shared/hocs/AuthGuard';
import ForcePasswordLayout from 'modules/shared/layouts/force-password/ForcePasswordLayout';
import MainLayout from 'modules/shared/layouts/main/MainLayout';
import AccountDeletionPage from 'modules/shared/pages/account-deletion/Page';
import InactivityPage from 'modules/shared/pages/inactivity/Page';
import MicrosoftErrorPage from 'modules/shared/pages/microsoft-error/Page';
import PrivacyPolicyPage from 'modules/shared/pages/privacy-policy/Page';
import TermsAndConditionPage from 'modules/shared/pages/terms-and-condition/Page';
import StorageIntegrationRouter from 'modules/storage-integration/StorageIntegrationRouter';
import SupportCenterRoutes from 'modules/support-center/SupportCenterRouter';
import ForceResetPasswordPage from 'modules/user-management/pages/force-reset-password/Page';
import UserManagementRoutes from 'modules/user-management/UserManagementRouter';
import AppLayout from './AppLayout';
import ForceMicrosoftAuthenticationPage from 'modules/plugins/pages/force-microsoft-authenticate/Page';
import FeatureGuidePage from 'modules/shared/features/release-announcements/pages/FeatureGuidePage';
import ReleaseAnnouncementsPage from 'modules/shared/features/release-announcements/pages/ReleaseAnnouncementsPage';

const router = createBrowserRouter([
    {
        path: '/',
        element: <AppLayout />,
        errorElement: <ErrorBoundary />,
        children: [
            { path: 'account-deletion', element: <AccountDeletionPage type={DeleteAccountTypeEnum.PROGRESS_CENTER} /> },
            {
                path: 'site-walk-account-deletion',
                element: <AccountDeletionPage type={DeleteAccountTypeEnum.SITE_WALK} />,
            },
            { path: 'privacy-policy', element: <PrivacyPolicyPage type={DeleteAccountTypeEnum.PROGRESS_CENTER} /> },
            {
                path: 'site-walk-privacy-policy',
                element: <PrivacyPolicyPage type={DeleteAccountTypeEnum.SITE_WALK} />,
            },
            {
                path: 'terms-and-condition',
                element: <TermsAndConditionPage type={DeleteAccountTypeEnum.PROGRESS_CENTER} />,
            },
            {
                path: 'site-walk-terms-and-condition',
                element: <TermsAndConditionPage type={DeleteAccountTypeEnum.SITE_WALK} />,
            },
            { path: 'error', element: <MicrosoftErrorPage /> },
            { path: 'inactivity', element: <InactivityPage /> },
            { path: '404', element: <PageNotFound /> },
            ...AuthRoutes,
            ...OnBoardingRoutes,
            ...PuppeteerRoutes,
            {
                path: '/',
                element: <AuthGuard page={<MainLayout />} />,
                children: [
                    ...DashboardRoutes,
                    ...UserManagementRoutes,
                    ...DocumentRoutes,
                    ...ProgressLineRouter,
                    ...ProgressLineCommunicationRouter,
                    ...PluginRoutes,
                    ...SupportCenterRoutes,
                    ...AccountRoutes,
                    ...StorageIntegrationRouter,
                    { path: 'whats-new', element: <ReleaseAnnouncementsPage /> },
                    { path: 'whats-new/features/:featureId/guide', element: <FeatureGuidePage /> },

                    { path: '404', element: <PageNotFound /> },
                ],
            },
            {
                path: '/',
                element: <AuthGuard page={<ForcePasswordLayout />} />,
                children: [
                    { path: 'suspicious-activity', element: <ForceResetPasswordPage /> },
                    {
                        path: 'force-microsoft-authenticate',
                        element: <ForceMicrosoftAuthenticationPage />,
                    },
                ],
            },

            { path: 'autodesk-integration-viewer', element: <AutodeskIntegrationViewerPage /> },
            { path: 'iframe-integration-viewer', element: <IFrameIntegrationViewerPage /> },
            { path: 'procore/embedded', element: <ProcoreEmbeddedLoginPage /> },
            { path: 'pulse-authorization', element: <PulseLoginPage /> },

            {
                path: 'plangrid-authorization',
                element: <PlanGridAuthorizationPage />,
            },
            {
                path: 'google-authorization',
                element: <GoogleAuthorizationPage />,
            },
            {
                path: 'google-authenticate',
                element: <GoogleAuthenticatePage />,
            },
            {
                path: 'google-onboarding-authenticate',
                element: <GoogleOnboardingAuthenticatePage />,
            },
            {
                path: 'microsoft-authorization',
                element: <MicrosoftAuthorizationPage />,
            },
            {
                path: 'microsoft-authenticate',
                element: <MicrosoftAuthenticatePage />,
            },
            {
                path: 'microsoft-onboarding-authenticate',
                element: <MicrosoftOnboardingAuthenticatePage />,
            },
            {
                path: 'autodesk-authorization',
                element: <AutodeskAuthorizationPage />,
            },
            {
                path: 'autodesk-authenticate',
                element: <AutokdeskAuthenticatePage />,
            },
            {
                path: 'autodesk-onboarding-authenticate',
                element: <AutodeskOnboardingAuthenticatePage />,
            },
            {
                path: 'gridwall-viewer/:token',
                element: <GridwallViewerPage />,
            },
            { path: '*', element: <PageNotFound /> },
        ],
    },
]);

const preloadPages = (routes: any[]) => {
    routes.forEach((route) => {
        if (typeof route.element.type.preload === 'function') {
            route.element.type.preload();
        }

        if (route.children) {
            preloadPages(route.children);
        }
    });
};

const StandaloneRoutes = [...PuppeteerRoutes.map((route) => route.path), 'autodesk-integration-viewer'];

const AppRouter = () => {
    useEffect(() => {
        // Don't preload the entire app for standalone routes like puppteer and integration viewers
        if (StandaloneRoutes.some((route) => window.location.pathname.startsWith(`/${route}`))) return;

        setTimeout(() => {
            preloadPages(router.routes);
        }, 0);
    }, []);

    return (
        <Suspense fallback={<PagePreloader />}>
            <RouterProvider router={router} />
        </Suspense>
    );
};

export default AppRouter;
